import React from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Typography,
  Stack,
  Container,
  Avatar,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import MailIcon from "@mui/icons-material/Mail";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from '../components/context/AuthContext';
import logo from "../Image/9rlogo.png";

// Navigation links
const navLinks = [
  { label: "Home", path: "/" },
  { label: "Email Sender", path: "/email-sender" },
  { label: "How to Use", path: "/how-to-use" },
  { label: "About", path: "/about" },
];

// Social media links
const socialLinks = [
  {
    href: "https://api.whatsapp.com/send?phone=+919925615191&text=Hi+9|R,+I+want+some+more+information+about+your+services.+Please+reply+soon.",
    icon: <WhatsAppIcon />,
    color: "#25D366",
  },
  {
    href: "https://www.facebook.com/profile.php?id=61563096922232",
    icon: <FacebookIcon />,
    color: "#1877F2",
  },
  {
    href: "https://www.instagram.com/9rnfc_com?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==",
    icon: <InstagramIcon />,
    color: "#E4405F",
  },
  {
    href: "https://www.linkedin.com/in/9r-nfc-b63aaa319",
    icon: <LinkedInIcon />,
    color: "#0A66C2",
  },
  {
    href: "mailto:9rnfccard@gmail.com",
    icon: <MailIcon />,
    color: "#EA4335",
  },
];

// Reusable Social Icon Component
const SocialIcon = ({ href, icon, color }) => (
  <IconButton
    href={href}
    target="_blank"
    rel="noreferrer"
    aria-label={`Connect via ${href.includes("whatsapp") ? "WhatsApp" : href.includes("facebook") ? "Facebook" : href.includes("instagram") ? "Instagram" : href.includes("linkedin") ? "LinkedIn" : "Email"}`}
    sx={{
      color,
      background: `rgba(${parseInt(color.slice(1, 3), 16), parseInt(color.slice(3, 5), 16), parseInt(color.slice(5, 7), 16)}, 0.08)`,
      border: `1px solid rgba(${parseInt(color.slice(1, 3), 16), parseInt(color.slice(3, 5), 16), parseInt(color.slice(5, 7), 16)}, 0.15)`,
      borderRadius: 2,
      width: 44,
      height: 44,
      "&:hover": {
        background: `rgba(${parseInt(color.slice(1, 3), 16), parseInt(color.slice(3, 5), 16), parseInt(color.slice(5, 7), 16)}, 0.2)`,
        transform: "scale(1.1) translateY(-2px)",
        boxShadow: `0 6px 16px rgba(${parseInt(color.slice(1, 3), 16), parseInt(color.slice(3, 5), 16), parseInt(color.slice(5, 7), 16)}, 0.4)`,
      },
      transition: "all 0.3s ease",
    }}
  >
    {icon}
  </IconButton>
);

const Navbar = React.memo(() => {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleDrawerToggle = () => setDrawerOpen(!drawerOpen);
  const handleProfileMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleProfileMenuClose = () => setAnchorEl(null);
  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
  };

  return (
    <AppBar
      position="sticky"
      color="transparent"
      elevation={0}
      sx={{
        background: "rgba(255,255,255,0.95)",
        backdropFilter: "blur(12px)",
        borderBottom: "1px solid rgba(0,0,0,0.1)",
        py: { xs: 0.5, sm: 1 },
        top: 0,
        zIndex: 1200,
        transition: "all 0.3s ease",
        "&:hover": {
          background: "rgba(255,255,255,0.98)",
          boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
        },
      }}
    >
      <Container maxWidth="xl">
        <Toolbar
          sx={{
            minHeight: { xs: 48, sm: 56, md: 64 },
            px: { xs: 1, sm: 2 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Logo and Brand */}
          <Box
            component={Link}
            to="/"
            sx={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              flexGrow: { xs: 1, md: 0 },
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="9remailsender logo"
              loading="lazy"
              sx={{
                height: { xs: 32, sm: 40, md: 44 },
                width: { xs: 32, sm: 40, md: 44 },
                mr: { xs: 1, sm: 1.5 },
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#1f2937",
                fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
                fontFamily: "'Inter', sans-serif",
                lineHeight: 1.2,
              }}
            >
              Email Sender
            </Typography>
          </Box>

          {/* Desktop Navigation */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: { md: 1, lg: 2 },
              borderRadius: 2,
              px: { md: 2, lg: 3 },
              py: 0.5,
              background: "rgba(37, 99, 235, 0.05)",
            }}
          >
            {navLinks.map((link) => (
              <Button
                key={link.path}
                component={Link}
                to={link.path}
                color={location.pathname === link.path ? "primary" : "inherit"}
                sx={{
                  px: { md: 2, lg: 3 },
                  py: 1,
                  fontWeight: location.pathname === link.path ? 700 : 500,
                  textTransform: "none",
                  fontSize: { md: "0.9rem", lg: "1rem" },
                  color: location.pathname === link.path ? "#1e40af" : "#374151",
                  borderRadius: 2,
                  background:
                    location.pathname === link.path
                      ? "rgba(37, 99, 235, 0.1)"
                      : "transparent",
                  "&:hover": {
                    background: "rgba(37, 99, 235, 0.15)",
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                {link.label}
              </Button>
            ))}

            {/* Authentication Buttons - Desktop */}
            {user ? (
              <Box sx={{ ml: { md: 2, lg: 3 } }}>
                <IconButton
                  onClick={handleProfileMenuOpen}
                  aria-label="User profile"
                  sx={{
                    p: 0.5,
                    background: "rgba(255,255,255,0.9)",
                    borderRadius: 2,
                    "&:hover": {
                      background: "rgba(255,255,255,1)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: "primary.main",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                    }}
                  >
                    {user.name?.charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleProfileMenuClose}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      borderRadius: 2,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <MenuItem
                    component={Link}
                    to="/profile"
                    onClick={handleProfileMenuClose}
                    sx={{ py: 1, px: 2 }}
                  >
                    <AccountCircleIcon sx={{ mr: 1, color: "#6b7280" }} />
                    Profile
                  </MenuItem>
                  <Divider />
                  <MenuItem
                    onClick={handleLogout}
                    sx={{ py: 1, px: 2, color: "#ef4444" }}
                  >
                    <LogoutIcon sx={{ mr: 1 }} />
                    Logout
                  </MenuItem>
                </Menu>
              </Box>
            ) : (
              <Box sx={{ ml: { md: 2, lg: 3 }, display: "flex", gap: 1 }}>
                <Button
                  component={Link}
                  to="/login"
                  variant="outlined"
                  sx={{
                    px: 2,
                    py: 0.75,
                    fontWeight: 500,
                    textTransform: "none",
                    borderRadius: 2,
                    color: "#2563eb",
                    borderColor: "rgba(37, 99, 235, 0.3)",
                    "&:hover": {
                      background: "rgba(37, 99, 235, 0.1)",
                      borderColor: "#2563eb",
                    },
                  }}
                >
                  Login
                </Button>
                <Button
                  component={Link}
                  to="/register"
                  variant="contained"
                  sx={{
                    px: 2,
                    py: 0.75,
                    fontWeight: 500,
                    textTransform: "none",
                    borderRadius: 2,
                    background: "linear-gradient(135deg, #667eea, #764ba2)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #5a6fd8, #6a4190)",
                    },
                  }}
                >
                  Register
                </Button>
              </Box>
            )}
          </Box>

          {/* Mobile Menu Button */}
          <IconButton
            edge="end"
            color="inherit"
            aria-label="Open navigation menu"
            onClick={handleDrawerToggle}
            sx={{ display: { xs: "flex", md: "none" }, color: "#374151" }}
          >
            <MenuIcon />
          </IconButton>

          {/* Mobile Drawer */}
          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={handleDrawerToggle}
            PaperProps={{
              sx: {
                width: { xs: 280, sm: 320 },
                background: "rgba(255,255,255,0.98)",
                backdropFilter: "blur(12px)",
              },
            }}
          >
            <Box sx={{ height: "100%", display: "flex", flexDirection: "column", p: 2 }}>
              {/* Mobile Header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                  pb: 2,
                  borderBottom: "1px solid rgba(0,0,0,0.1)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box
                    component="img"
                    src={logo}
                    alt="9remailsender logo"
                    loading="lazy"
                    sx={{ height: 36, width: 36, mr: 1.5 }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#1f2937",
                      fontSize: "1.25rem",
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    Email Sender
                  </Typography>
                </Box>
                <IconButton
                  onClick={handleDrawerToggle}
                  aria-label="Close navigation menu"
                  sx={{ color: "#6b7280" }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Mobile Navigation Links */}
              <List sx={{ mb: 3 }}>
                {navLinks.map((link) => (
                  <ListItem
                    button
                    key={link.path}
                    component={Link}
                    to={link.path}
                    selected={location.pathname === link.path}
                    onClick={handleDrawerToggle}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      py: 1.5,
                      color: location.pathname === link.path ? "#1e40af" : "#374151",
                      background:
                        location.pathname === link.path ? "rgba(37, 99, 235, 0.1)" : "transparent",
                      "&:hover": {
                        background: "rgba(37, 99, 235, 0.15)",
                        transform: "translateX(2px)",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    <ListItemText
                      primary={link.label}
                      primaryTypographyProps={{
                        fontWeight: location.pathname === link.path ? 700 : 500,
                        fontSize: "1rem",
                        fontFamily: "'Inter', sans-serif",
                      }}
                    />
                  </ListItem>
                ))}
              </List>

              {/* Mobile Authentication Section */}
              {user ? (
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 1.5,
                      background: "rgba(37, 99, 235, 0.05)",
                      borderRadius: 2,
                      mb: 2,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: "primary.main",
                        fontSize: "1rem",
                        mr: 1.5,
                      }}
                    >
                      {user.name?.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: "#1f2937",
                          fontFamily: "'Inter', sans-serif",
                        }}
                      >
                        {user.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#6b7280" }}>
                        {user.email}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    fullWidth
                    component={Link}
                    to="/profile"
                    onClick={handleDrawerToggle}
                    variant="outlined"
                    startIcon={<AccountCircleIcon />}
                    sx={{
                      mb: 1,
                      py: 1,
                      fontWeight: 500,
                      textTransform: "none",
                      borderRadius: 2,
                      color: "#2563eb",
                      borderColor: "rgba(37, 99, 235, 0.3)",
                      "&:hover": {
                        background: "rgba(37, 99, 235, 0.1)",
                      },
                    }}
                  >
                    Profile Settings
                  </Button>
                  <Button
                    fullWidth
                    onClick={() => {
                      handleLogout();
                      handleDrawerToggle();
                    }}
                    variant="outlined"
                    startIcon={<LogoutIcon />}
                    sx={{
                      py: 1,
                      fontWeight: 500,
                      textTransform: "none",
                      borderRadius: 2,
                      color: "#ef4444",
                      borderColor: "rgba(239, 68, 68, 0.3)",
                      "&:hover": {
                        background: "rgba(239, 68, 68, 0.1)",
                      },
                    }}
                  >
                    Logout
                  </Button>
                </Box>
              ) : (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 2,
                      textAlign: "center",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Get Started
                  </Typography>
                  <Button
                    fullWidth
                    component={Link}
                    to="/login"
                    onClick={handleDrawerToggle}
                    variant="outlined"
                    sx={{
                      mb: 1,
                      py: 1,
                      fontWeight: 500,
                      textTransform: "none",
                      borderRadius: 2,
                      color: "#2563eb",
                      borderColor: "rgba(37, 99, 235, 0.3)",
                      "&:hover": {
                        background: "rgba(37, 99, 235, 0.1)",
                      },
                    }}
                  >
                    Login
                  </Button>
                  <Button
                    fullWidth
                    component={Link}
                    to="/register"
                    onClick={handleDrawerToggle}
                    variant="contained"
                    sx={{
                      py: 1,
                      fontWeight: 500,
                      textTransform: "none",
                      borderRadius: 2,
                      background: "linear-gradient(135deg, #667eea, #764ba2)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #5a6fd8, #6a4190)",
                      },
                    }}
                  >
                    Register
                  </Button>
                </Box>
              )}

              {/* Social Media in Mobile Drawer */}
              <Box sx={{ mt: "auto", pt: 2, borderTop: "1px solid rgba(0,0,0,0.1)" }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 2,
                    textAlign: "center",
                    fontWeight: 600,
                    color: "#374151",
                  }}
                >
                  Connect with us
                </Typography>
                <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mb: 2 }}>
                  {socialLinks.map((link) => (
                    <SocialIcon key={link.href} {...link} />
                  ))}
                </Stack>
                <Typography
                  variant="caption"
                  sx={{
                    textAlign: "center",
                    display: "block",
                    fontWeight: 500,
                    color: "#6b7280",
                  }}
                >
                  Email: 9rnfccard@gmail.com
                </Typography>
              </Box>
            </Box>
          </Drawer>
        </Toolbar>
      </Container>
    </AppBar>
  );
});

export default Navbar;